/**
 * File: project.ts
 * Description: TypeScript type definitions for Project entities based on backend API schemas
 * Author: GitHub Copilot
 * Created: 2025-08-01
 * Last Modified: 2025-08-01
 */

// ====================
// Project Types
// ====================

export enum ProjectType {
  GREEN_FIELD = 'green_field',
  BROWN_FIELD = 'brown_field'
}

export enum ProjectStatus {
  CONTEXT_UPLOAD = 'context_upload',
  CONTEXT_REVIEW = 'context_review',
  ADR = 'adr',
  GENERATION = 'generation',
  BIZZDESIGN_INTEGRATION = 'bizzdesign_integration',
  IN_REVIEW = 'in_review',
  COMPLETED = 'completed',
  PUBLISHED = 'published'
}

export type ProjectReviewStatus =
  | 'not_submitted'
  | 'in_review'
  | 'approved'
  | 'changes_requested'
  | 'closed';

// ====================
// API Schema Types (matching backend)
// ====================

export interface ProjectCreate {
  title: string;
  description: string;
  project_type: ProjectType;
  line_of_business?: string | null;
  status?: string;
  original_link?: string;
}

export interface ProjectUpdate {
  title?: string;
  description?: string;
  line_of_business?: string | null;
  status?: string;
  progress?: ProjectProgress;
  bizzdesign_url?: string | null;
  ear_url?: string | null;
  ear_wiki_url?: string | null;
}

export interface ProjectReviewSummary {
  review_id: string | null;
  status: ProjectReviewStatus;
  reviewer_count: number;
  approver_count: number;
  approver_missing: boolean;
  approver_names: string[];
  approved_at: string | null;
  updated_at: string | null;
}

export interface ProjectPublishSummary {
  is_published: boolean;
  can_publish: boolean;
  published_by: string | null;
  published_at: string | null;
}

export interface ProjectResponse {
  id: number;
  title: string;
  description: string;
  project_type: ProjectType;
  line_of_business: string | null;
  status: ProjectStatus | string;
  original_link: string | null;
  bizzdesign_url?: string | null;
  ear_url?: string | null;
  ear_wiki_url?: string | null;
  owner: string;
  owner_display_name: string | null;
  co_owners: string | null;
  progress: ProjectProgress | null;
  review_summary: ProjectReviewSummary;
  publish_summary: ProjectPublishSummary;
  /**
   * Version label assigned once a project is approved by all approvers
   * (e.g. "v1"). Absent for projects that are not yet approved.
   */
  version?: string | null;
  created_at: string;
  updated_at: string;
}

export interface ProjectFileResponse {
  id: string;
  name: string;
  url: string;
  blob_path: string | null;
  size: number;
  type: string;
}

// ====================
// Frontend Types (enhanced for UI)
// ====================

export interface ProjectItem extends ProjectResponse {
  progress: ProjectProgress;
  projectType?: ProjectType;
  projectLineOfBusiness?: string | null;
  originalProjectLink?: string;
}

export interface ProjectProgress {
  contextUploaded: boolean;
  contextReviewed: boolean;
  architectureGenerated: boolean;
  sectionsCompleted: number;
  totalSections: number;
}

export interface ProjectSection {
  id: string;
  title: string;
  content: string;
}

// ====================
// Context Management Types (matching backend schemas)
// ====================

export enum ProjectContextUploadedDocumentType {
  BUSINESS_REQUIREMENTS_DOCUMENT = 'business_requirements_document',
  GUIDELINES = 'guidelines',
  SAHLD = 'sahld',
  C4_DIAGRAM = 'c4_diagram',
  ADDITIONAL = 'additional_context',
  MEETING_TRANSCRIPT = 'meeting_transcript',
  OTHER = 'other'
}

export interface ProjectContextUploadedDocumentResponse {
  type: 'ProjectContextUploadedDocument';
  name: string;
  blob_path: string;
  document_type: ProjectContextUploadedDocumentType;
  updated_at?: string;
}

export interface ProjectContextReferenceDocumentResponse {
  type: 'ProjectContextReferenceDocument';
  file_url: string;
  updated_at?: string;
}

export interface ProjectContextInstructionsResponse {
  type: 'ProjectContextInstructions';
  instructions: string;
  updated_at?: string;
}

export type ProjectContextResponse = 
  | ProjectContextUploadedDocumentResponse 
  | ProjectContextReferenceDocumentResponse 
  | ProjectContextInstructionsResponse;

export interface ProjectContextReferenceDocumentCreate {
  type: 'ProjectContextReferenceDocument';
  file_url: string;
}

export interface ProjectContextInstructionsCreate {
  type: 'ProjectContextInstructions';
  instructions: string;
}
